import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit
} from '@angular/core';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';
import {
  PlatformInformationService,
  PlatformurlPipe
} from '../../../../../../projects/platform-manager/src/public_api';
import { CommonModule } from '@angular/common';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  arrowRight,
  boots,
  changeEquipment,
  chevronDown,
  circleNotch,
  cross,
  damage,
  depot,
  helmet,
  IconComponent,
  infoCircle,
  provideIcons,
  rightArrow,
  smView,
  star2,
  starOutline,
  stop,
  tick
} from '../../../../shared/icons';
import { Addon, Cart, Product } from '../../services/entities';
import { CurrencyPipe } from '../../../../core/pipes/currency.pipe';
import { RouterModule } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ProductQualityPipe } from '../../pipes/product-quality.pipe';
import { ModifyStarsPipe } from '../../pipes/modify-stars.pipe';
import { alpiTooltipDirective } from '../../../../shared/components/alpi-tooltip/alpiTooltip';
import { CurrencyService } from '../../../../core/services/currency.service';
import { CartService } from '../../services/cart';
import { merge, tap } from 'rxjs';
import { ToggleComponent } from '../toggle/toggle.component';

@Component({
  selector: 'app-product-item',
  imports: [
    CommonModule,
    AlpitranslatePipe,
    IconComponent,
    ToggleComponent,
    CurrencyPipe,
    RouterModule,
    ProductQualityPipe,
    ModifyStarsPipe,
    alpiTooltipDirective,
    PlatformurlPipe
  ],
  providers: [
    provideIcons({
      starOutline,
      star2,
      stop,
      smView,
      chevronDown,
      circleNotch,
      infoCircle,
      boots,
      helmet,
      depot,
      damage,
      arrowRight,
      rightArrow,
      changeEquipment,
      tick,
      cross
    })
  ],
  templateUrl: './product-item.component.html',
  styleUrl: './product-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('clamp', [
      state('closed', style({ '-webkit-line-clamp': 2, height: '*' })),
      state('open', style({ '-webkit-line-clamp': 'initial', 'margin-bottom': '.5rem' })),
      transition('closed => open', [style({ height: '2rem' }), animate('200ms ease-in-out')])
    ])
  ]
})
export class ProductItemComponent implements OnInit {
  @Input({ required: true }) cart: Cart;
  @Input({ required: true }) product: Product;
  @Input({ required: true }) companyId: number;
  @Input({ required: true }) isSelected: boolean;

  isMobile = this.resolutionService.isSmallerThanLargeDevice();
  toggleDescription: 'open' | 'closed' = 'closed';

  showAlpiQualityCategoryDesignation = false;

  constructor(
    public platformInformationService: PlatformInformationService,
    public translateService: TranslateService,
    public currencyService: CurrencyService,
    private resolutionService: ResolutionService,
    private cartService: CartService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    merge(
      this.resolutionService.sizeReferenceChange.pipe(
        tap(() => (this.isMobile = this.resolutionService.isSmallerThanLargeDevice()))
      ),
      this.currencyService.onCurrencyChange,
      this.translateService.onTranslationChange,
      this.cartService.cart$.pipe(
        tap(() => {
          const selectedProduct = this.cart.selectedPerson.productEntities.find(
            productEntity => productEntity.definitionId === this.product.definitionId
          );
          if (selectedProduct) {
            this.product.addons.forEach(addon => {
              addon.isSelected = selectedProduct.addons.some(ad => ad.definitionId === addon.definitionId);
            });
          } else {
            this.product.addons.forEach(a => (a.isSelected = false));
          }
        })
      ),
      this.platformInformationService.data$.pipe(
        tap(
          data =>
            (this.showAlpiQualityCategoryDesignation =
              this.platformInformationService.getCurrentPlatformShortcode() === 'es'
                ? false
                : data.show_alpi_quality_category_designation)
        )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdr.markForCheck();
      });
  }

  selectProductWithSelectedAddons() {
    this.cartService.updateProduct(this.cart.selectedPerson, this.product);
  }

  changeAddon(addon: Addon) {
    addon.isSelected = !addon.isSelected;
    if (this.cart.selectedPerson.productEntities.find(pE => pE.definitionId === this.product.definitionId)) {
      this.cartService.updateProduct(this.cart.selectedPerson, this.product);
    }
  }

  removeProduct() {
    this.cartService.removeProduct(this.cart.selectedPerson, this.product.definitionId);
  }

  getQuality(product: Product): number {
    return +product.qualityCategoryAbbreviation.replace('*', '');
  }
}
